"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useGetDealerList, useGetCityList } from "@/lib/api/hooks";
import { useI18n, getLocalizedField } from "@/i18n";

const FEATURED_CARS = [
    {
        title: "2021 Toyota RAV4 Hybrid",
        img: "https://images.unsplash.com/photo-1583121274602-3e2820c69888?w=800&q=80",
        sub: "📍 Conakry · 2.5L",
        price: "GNF 285M",
        photos: 8,
        badges: [<span key="b" className="tag tag-brand">经销商</span>],
    },
    {
        title: "2019 Mercedes-Benz C200",
        img: "https://images.unsplash.com/photo-1542362567-b07e54358753?w=800&q=80",
        sub: "📍 Conakry · 2.0L",
        price: "GNF 650M",
        photos: 12,
        badges: [<span key="b1" className="tag tag-brand">经销商</span>, <span key="b2" className="tag tag-success">推广</span>],
    },
    {
        title: "2018 Hyundai Tucson",
        img: "https://images.unsplash.com/photo-1606220588913-b3aacb4d2f46?w=800&q=80",
        sub: "📍 Conakry · 2.0L",
        price: "NEGOTIABLE",
        negotiable: true,
        photos: 5,
        badges: [<span key="b" className="tag tag-brand">经销商</span>],
    },
    {
        title: "2020 Nissan Patrol",
        img: "https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=800&q=80",
        sub: "📍 Conakry · 4.0L",
        price: "GNF 890M",
        photos: 6,
        badges: [<span key="b" className="tag tag-brand">经销商</span>],
    },
    {
        title: "2024 BYD ATTO 3",
        img: "https://images.unsplash.com/photo-1617886322207-6f504e7472c5?w=800&q=80",
        sub: "📍 Conakry · EV",
        price: "GNF 480M",
        photos: 10,
        badges: [<span key="b1" className="tag tag-brand">经销商</span>, <span key="b2" className="tag tag-new">EV</span>],
    },
    {
        title: "2019 BMW X5 xDrive",
        img: "https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=800&q=80",
        sub: "📍 Conakry · 3.0L",
        price: "GNF 720M",
        photos: 7,
        badges: [<span key="b" className="tag tag-brand">经销商</span>],
    },
    {
        title: "2020 Kia Sportage",
        img: "https://images.unsplash.com/photo-1568844293986-8d0400bd4745?w=800&q=80",
        sub: "📍 Conakry · 2.0L",
        price: "GNF 240M",
        photos: 6,
        badges: [<span key="b" className="tag tag-brand">经销商</span>],
    },
    {
        title: "2023 Tesla Model 3",
        img: "https://images.unsplash.com/photo-1556800572-1b8aeef2c54f?w=800&q=80",
        sub: "📍 Conakry · EV",
        price: "GNF 750M",
        photos: 9,
        badges: [<span key="b1" className="tag tag-brand">经销商</span>, <span key="b2" className="tag tag-success">推广</span>],
    },
];
export default function DealersPage() {
    const { t, lang } = useI18n();
    const SORT_OPTIONS = [
        { value: "rating_desc", label: t("dealers.sortRating") },
        { value: "sold_desc", label: t("dealers.sortSold") },
        { value: "response_desc", label: t("dealers.sortResponse") },
        { value: "latest", label: t("dealers.sortLatest") },
    ];
    const router = useRouter();
    const [searchInput, setSearchInput] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedCityId, setSelectedCityId] = useState(null);
    const [sortBy, setSortBy] = useState("rating_desc");
    const [view, setView] = useState("grid");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(12);
    const [searchFocused, setSearchFocused] = useState(false);

    const { data: cityData } = useGetCityList();
    const cityList = cityData?.data ?? [];

    const handleSearch = () => {
        setSearchQuery(searchInput);
        setCurrentPage(1);
    };

    const handleCityClick = (cityId) => {
        setSelectedCityId(cityId);
        setCurrentPage(1);
    };

    const cityId = selectedCityId ?? undefined;

    // 阻止浏览器滚动恢复，页面刷新始终在顶部
    useEffect(() => {
        if ('scrollRestoration' in history) {
            history.scrollRestoration = 'manual';
        }
        // 立即 + 延迟 双重确保覆盖 Next.js 的滚动恢复
        window.scrollTo(0, 0);
        // 延迟到浏览器完成滚动恢复后再拉一次
        requestAnimationFrame(() => window.scrollTo(0, 0));
        // pageshow 用于处理 bfcache 恢复
        const onShow = () => window.scrollTo(0, 0);
        window.addEventListener('pageshow', onShow);
        return () => window.removeEventListener('pageshow', onShow);
    }, []);

    const { data, isLoading } = useGetDealerList({
        page: currentPage,
        size: pageSize,
        keyword: searchQuery,
        cityId,
        sort: sortBy
    });

    // client.js parseResponse 将分页结构扁平化：data = records 数组，pagination = { page, limit, total, totalPages }
    const dealerList = data?.data ?? [];
    const totalPages = data?.pagination?.total ? Math.ceil(data.pagination.total / pageSize) : 1;

    // 骨架屏占位：生成 pageSize 个空卡片
    const skeletonArray = Array.from({ length: pageSize }, (_, i) => i);
    return (<div className="dealers-page">
      <div className="page-header">
        <div className="container">
          <div className="breadcrumb">
            <Link href="/">{t("common.home")}</Link>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 18 6-6-6-6"/></svg>
            <span>{t("nav.dealers")}</span>
          </div>
          <h1 className="page-title">{t("dealers.title")}</h1>
          <p className="page-subtitle">{t("dealers.subtitle", { count: data?.pagination?.total ?? 0 })}</p>
        </div>
      </div>

      <div className="container">
        <div className="filter-bar mb-6" style={{ borderRadius: "var(--radius-md)", border: "1px solid var(--border)", paddingLeft: 16, paddingRight: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 8px 7px 16px", border: "1px solid var(--color-border)", outline: searchFocused ? "2px solid var(--color-brand)" : "none", outlineOffset: searchFocused ? 2 : 0, borderRadius: "var(--radius)", background: "var(--surface)", height: 40, transition: "outline 300ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 300ms cubic-bezier(0.16, 1, 0.3, 1)", boxShadow: searchFocused ? "0 0 0 4px rgba(59, 130, 246, 0.12)" : "none" }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16" style={{ color: "var(--text-muted)", flexShrink: 0 }}><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <input
              type="text"
              placeholder={t("dealers.searchPlaceholder")}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              style={{ border: "none", outline: "none", background: "transparent", fontSize: 14, color: "var(--text)", width: 160, minWidth: 0, lineHeight: 1 }}
            />
            <button onClick={handleSearch} style={{ height: 30, padding: "0 14px", border: "1px solid var(--color-primary)", borderRadius: "var(--radius)", background: "var(--color-primary)", color: "white", fontSize: 13, cursor: "pointer", lineHeight: "28px", whiteSpace: "nowrap", flexShrink: 0, transition: "background 200ms" }}>{t("common.search")}</button>
          </div>
          <button className={`filter-chip ${selectedCityId === null ? "active" : ""}`} onClick={() => handleCityClick(null)}>{t("dealers.allCities")}</button>
          {cityList.map((city) => (<button key={city.id} className={`filter-chip ${selectedCityId === city.id ? "active" : ""}`} onClick={() => handleCityClick(city.id)}>
              {getLocalizedField(city, "name", lang)}
            </button>))}
          <div className="filter-sort">
            <span>{t("dealers.sortLabel")}</span>
            <select value={sortBy} onChange={(e) => { setSortBy(e.target.value); setCurrentPage(1); }}>
              {SORT_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
            </select>
            <button className="view-toggle-btn" onClick={() => setView(view === "grid" ? "list" : "grid")} aria-label="Toggle view">
              {view === "grid" ? (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>) : (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>)}
            </button>
          </div>
        </div>

        <div style={{ paddingTop: 20 }}>
          {isLoading ? (
            <div className="grid grid-cols-3" style={{ gap: "var(--space-6)" }}>
              {skeletonArray.map((i) => (
                <div key={i} className="card" style={{ padding: 24, overflow: "hidden", animation: "pulse 1.5s ease-in-out infinite" }}>
                  <div style={{ display: "flex", gap: 16, marginBottom: 20 }}>
                    <div style={{
                      width: 56,
                      height: 56,
                      borderRadius: "var(--radius)",
                      background: "var(--color-border)",
                      flexShrink: 0,
                    }}/>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ width: "70%", height: 24, background: "var(--color-border)", borderRadius: 4, marginBottom: 10 }}/>
                      <div style={{ width: "100%", height: 16, background: "var(--color-border)", borderRadius: 4, opacity: 0.5 }}/>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
                    <div style={{ width: 80, height: 16, background: "var(--color-border)", borderRadius: 4, opacity: 0.5 }}/>
                    <div style={{ width: 100, height: 16, background: "var(--color-border)", borderRadius: 4, opacity: 0.5 }}/>
                  </div>
                  <div className="stats-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 16 }}>
                    <div className="stat-card" style={{ padding: 12, textAlign: "center", background: "var(--color-border)", opacity: 0.3 }}/>
                    <div className="stat-card" style={{ padding: 12, textAlign: "center", background: "var(--color-border)", opacity: 0.3 }}/>
                    <div className="stat-card" style={{ padding: 12, textAlign: "center", background: "var(--color-border)", opacity: 0.3 }}/>
                  </div>
                  <div style={{ display: "flex", gap: 8, paddingTop: 16, borderTop: "1px solid var(--border)" }}>
                    <div style={{ flex: 1, height: 38, background: "var(--color-border)", borderRadius: "var(--radius)", opacity: 0.3 }}/>
                    <div style={{ width: 38, height: 38, background: "var(--color-border)", borderRadius: "var(--radius)", opacity: 0.3 }}/>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            view === "grid" ? (<div className="grid grid-cols-3" style={{ gap: "var(--space-6)" }}>
                {dealerList.map((d, i) => (<DealerCard key={d.id} index={i} dealer={d} onOpen={() => router.push(`/dealers/${d.id}`)}/>))}
              </div>) : (<div style={{ display: "flex", flexDirection: "column", gap: "var(--space-5)" }}>
                {dealerList.map((d, i) => (<DealerListItem key={d.id} index={i} dealer={d} onOpen={() => router.push(`/dealers/${d.id}`)}/>))}
              </div>)
          )}
        </div>

        <div className="pagination mt-8 mb-12">
          <button
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage(1)}
          >«</button>
          <button
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage(prev => prev - 1)}
          >‹</button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }
            return (
              <button
                key={pageNum}
                className={currentPage === pageNum ? "active" : ""}
                onClick={() => setCurrentPage(pageNum)}
              >{pageNum}</button>
            );
          })}
          <button
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage(prev => prev + 1)}
          >›</button>
          <button
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage(totalPages)}
          >»</button>
        </div>
      </div>

      </div>);
}

const LOGO_GRADIENTS = [
        "linear-gradient(135deg,#3DA005,#53c906)",
        "linear-gradient(135deg,#0EA5E9,#38BDF8)",
        "linear-gradient(135deg,#F59E0B,#FBBF24)",
        "linear-gradient(135deg,#8B5CF6,#A78BFA)",
        "linear-gradient(135deg,#FF7900,#FF3D00)",
        "linear-gradient(135deg,#2962FF,#5C7CFA)",
    ];

  const getInitials = (name) => {
      if (!name) return "D";
      return name.trim().slice(0, 2).toUpperCase();
  };

function DealerCard({ dealer, onOpen , index}) {
    const { t, lang } = useI18n();
    const logoIndex = index % LOGO_GRADIENTS.length;
    const initials = getInitials((function(d){const n=getLocalizedField(d,"dealerName",lang);if(typeof n==="string"&&n.trim().length)return n;return d.dealerNameZh||"";}(dealer)));
    return (<div className="card" style={{ padding: 0, overflow: "hidden", cursor: "pointer" }} onClick={onOpen}>
      <div style={{ padding: 24, display: "flex", gap: 16 }}>
        <div style={{
            width: 56,
            height: 56,
            borderRadius: "var(--radius)",
            background: LOGO_GRADIENTS[logoIndex],
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 20,
            fontWeight: 800,
            flexShrink: 0,
        }}>
          {initials}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{(function(d){const n=getLocalizedField(d,"dealerName",lang);if(typeof n==="string"&&n.trim().length)return n;return d.dealerNameZh||"";}(dealer))}</h3>
            {dealer.isVerified === 1 && <span className="tag tag-success" style={{ whiteSpace: "nowrap", flexShrink: 0 }}>{t("dealers.verified")}</span>}
          </div>
          <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0, lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{(function(d){return getLocalizedField(d,"description",lang)||d.descriptionZh||"";}(dealer))}</p>
        </div>
      </div>

      <div style={{ padding: "0 24px 24px" }}>
        <div style={{ display: "flex", gap: 12, marginBottom: 16, fontSize: 12, color: "var(--text-muted)", flexWrap: "wrap" }}>
          <span>📍 {dealer.cityName}</span>
          <span>📅 {t("dealers.joinDate")} {dealer.createTime?.split('T')[0]}</span>
        </div>

        <div className="stats-grid" style={{ gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 16 }}>
          <div className="stat-card" style={{ padding: 12, textAlign: "center" }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: "var(--text)" }}>{dealer.onSaleCount}</div>
            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{t("dealers.forSale")}</div>
          </div>
          <div className="stat-card" style={{ padding: 12, textAlign: "center" }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: "var(--primary)" }}>{dealer.totalSold ?? 0}</div>
            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{t("dealers.totalSold")}</div>
          </div>
          <div className="stat-card" style={{ padding: 12, textAlign: "center" }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: "var(--brand)" }}>{dealer.rating} ★</div>
            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{t("dealers.rating")}</div>
          </div>
          <div className="stat-card" style={{ padding: 12, textAlign: "center" }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: "var(--success)" }}>{(dealer.responseRate * 100).toFixed(0)}%</div>
            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{t("dealers.responseRate")}</div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, paddingTop: 16, borderTop: "1px solid var(--border)" }}>
          <button className="btn" style={{ flex: 1, height: 38, fontSize: 13, border: "1px solid #D0D5D0", background: "var(--surface)", color: "var(--text-secondary)" }} onClick={(e) => { e.stopPropagation(); onOpen(); }}>
            {t("dealers.viewShop")}
          </button>
          <button className="btn" aria-label="Favorite" style={{ width: 38, height: 38, padding: 0, border: "1px solid var(--border)", background: "var(--surface)", color: "var(--text-muted)", flexShrink: 0, fontSize: 18, lineHeight: 1, cursor: "pointer", borderRadius: "var(--radius)" }}>♡</button>
        </div>
      </div>
    </div>);
}
function DealerListItem({ dealer, onOpen , index}) {
    const { t, lang } = useI18n();
    const logoIndex = index % LOGO_GRADIENTS.length;
    const initials = getInitials((function(d){const n=getLocalizedField(d,"dealerName",lang);if(typeof n==="string"&&n.trim().length)return n;return d.dealerNameZh||"";}(dealer)));
    return (<div className="card" style={{ display: "flex", padding: 0, overflow: "hidden", cursor: "pointer" }} onClick={onOpen}>
      <div style={{
            width: 220,
            flexShrink: 0,
            background: "linear-gradient(135deg, #0F172A 0%, #1E293B 100%)",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        }}>
        <div style={{
            width: 80,
            height: 80,
            borderRadius: "var(--radius)",
            background: LOGO_GRADIENTS[logoIndex],
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 28,
            fontWeight: 800,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        }}>
          {initials}
        </div>
        {dealer.isVerified === 1 && (<span className="tag tag-success" style={{ position: "absolute", top: 16, left: 16 }}>{t("dealers.verified")}</span>)}
      </div>
      <div style={{ flex: 1, padding: 24, display: "flex", flexDirection: "column", gap: 12 }}>
        <div>
          <h3 style={{ fontSize: 20, fontWeight: 700, margin: 0, marginBottom: 6 }}>{(function(d){const n=getLocalizedField(d,"dealerName",lang);if(typeof n==="string"&&n.trim().length)return n;return d.dealerNameZh||"";}(dealer))}</h3>
          <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0 }}>{(function(d){return getLocalizedField(d,"description",lang)||d.descriptionZh||"";}(dealer))}</p>
        </div>
        <div style={{ display: "flex", gap: 16, fontSize: 12, color: "var(--text-muted)", flexWrap: "wrap" }}>
          <span>📍 {dealer.cityName}</span>
          <span>📅 {t("dealers.joinDate")} {dealer.createTime?.split('T')[0]}</span>
        </div>
        <div style={{ display: "flex", gap: 16, paddingTop: 12, borderTop: "1px solid var(--border)" }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "var(--text)" }}>{dealer.onSaleCount}</div>
            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{t("dealers.forSale")}</div>
          </div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "var(--brand)" }}>{dealer.rating} ★</div>
            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{t("dealers.rating")}</div>
          </div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "var(--success)" }}>{(dealer.responseRate * 100).toFixed(0)}%</div>
            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{t("dealers.responseRate")}</div>
          </div>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: 24, justifyContent: "center", minWidth: 140, borderLeft: "1px solid var(--border)" }}>
        <button className="btn btn-primary" onClick={(e) => { e.stopPropagation(); onOpen(); }}>{t("dealers.viewShop")}</button>
        <button className="btn btn-ghost" onClick={(e) => e.stopPropagation()}>WhatsApp</button>
      </div>
    </div>);
}
